<script>
import { getContrastingTextColor } from '@chatwoot/utils';

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    bgColor: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
  },
  emits: ['remove'],
  computed: {
    textColor() {
      if (this.variant === 'smooth') return '';
      if (this.variant === 'dashed') return '';
      return this.color || getContrastingTextColor(this.bgColor);
    },
    useTintedBackground() {
      return this.variant === 'smooth' && this.color && !this.bgColor;
    },
    labelClass() {
      return `label ${this.colorScheme} ${this.variant} ${
        this.small ? 'small' : ''
      }`;
    },
    labelStyle() {
      if (this.bgColor) {
        return {
          background: this.bgColor,
          color: this.textColor,
          border: `1px solid ${this.bgColor}`,
        };
      }
      if (this.useTintedBackground) {
        return {
          background: this.color + '1A',
          color: this.color,
          border: 'none',
        };
      }
      return {};
    },
    anchorStyle() {
      if (this.bgColor) {
        return { color: this.textColor };
      }
      return {};
    },
  },
  methods: {
    onClick() {
      this.$emit('remove', this.title);
    },
  },
};
</script>

<template>
  <div
    class="inline-flex items-center group/label"
    :class="labelClass"
    :style="labelStyle"
    :title="description"
  >
    <span v-if="icon" class="label-action--button">
      <fluent-icon :icon="icon" size="12" class="label--icon cursor-pointer" />
    </span>
    <span
      v-if="['smooth', 'dashed'].includes(variant) && title && !icon"
      :style="{ background: color }"
      class="label-color-dot"
    />
    <span v-if="!href" class="label-title">
      {{ title }}
    </span>
    <a v-else :href="href" :style="anchorStyle">{{ title }}</a>
    <button
      v-if="showClose"
      class="label-close--button"
      @click.stop="onClick"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    </button>
  </div>
</template>

<style scoped lang="scss">
.label {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  height: 20px;
  padding: 2px 6px 2px 4px;
  gap: 3px;
  border-radius: 29px;
  background: var(--color-bg-light, #F5F5F5);
  color: var(--color-body, #1E293B);
  border: none;
  white-space: nowrap;

  &.small {
    height: 16px;
    font-size: 11px;
    padding: 1px 5px 1px 3px;
    gap: 2px;
  }

  a {
    font-size: 12px;
    color: inherit;
    &:hover {
      text-decoration: underline;
    }
  }

  /* Color Schemes */
  &.primary {
    background: #DBEAFE;
    color: #1D4ED8;
    .label-color-dot { background: #3B82F6; }
  }
  &.secondary {
    background: #F1F5F9;
    color: #475569;
    .label-color-dot { background: #64748B; }
  }
  &.success {
    background: #D1FAE5;
    color: #047857;
    .label-color-dot { background: #10B981; }
  }
  &.alert {
    background: #FEE2E2;
    color: #B91C1C;
    .label-color-dot { background: #EF4444; }
  }
  &.warning {
    background: #FEF3C7;
    color: #B45309;
    .label-color-dot { background: #F59E0B; }
  }

  &.smooth {
    background: transparent;
    color: var(--color-body, #64748B);
    border: none;
  }

  &.dashed {
    background: transparent;
    color: var(--color-body, #64748B);
    border: 1px dashed #D1D5DB;
  }
}

.label-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
}

.label-close--button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  overflow: hidden;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  color: inherit;
  opacity: 0;
  transition: width 0.2s ease, opacity 0.15s ease, margin 0.2s ease;
}

.group\/label:hover .label-close--button {
  width: 14px;
  margin-left: 2px;
  opacity: 0.6;
}

.group\/label:hover .label-close--button:hover {
  opacity: 1;
}

.label-action--button {
  display: flex;
  margin-right: 2px;
}

.label-color-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.label.small .label-color-dot {
  width: 7px;
  height: 7px;
  min-width: 7px;
}
</style>
